import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { unwrapResult } from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { CustomAlert, CustomButton, CustomCheckBox, CustomSelect, CustomTable } from '../../../../shared/uiComponents';
import { errorNormalizer } from '../../../../shared/Helpers/functions';
import { ReportFilters, downloadFile } from '../../helpers';
import { Headers, TableBody } from './tableInfo';
import MBHStatus from './mbhLogsStatus';
import DownloadAlertContent from './downloadAlert';
import { REPORT_GENERATION_MESSAGE } from '../../../../shared/Helpers/constants';

import { DispatchProperties, useSelector } from '../../../../redux/store';
import { GetBillingProperties } from '../../../../redux/API/ClientAPIHelpers/billingProperties';
import { downloadMBHExcelReport, getBillingSessions } from '../../../../redux/State/clientSlice/billingSlice';

const MBHBillingStatuses = [
	{ id: '', name: 'All' },
	{ id: '0', name: 'Not Billed' },
	{ id: '1', name: 'Approved' },
	{ id: '2', name: 'Billed' },
];

const MBHReport = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const [params] = useSearchParams();
	const [mbhFormat, setMbhFormat] = useState<boolean>(true);
	const [mbhBillingStatus, setMbhBillingStatus] = useState<string>('');
	const [showDownloadAlert, setShowDownloadAlert] = useState<boolean>(false);

	const sessions = useSelector((state) => state.billing.mbhSessions);
	const loading = useSelector((state) => state.billing.loading);
	const { endDate, startDate, userId, onlyNew, clientIds, timeError } = useSelector(
		(state) => state.billing.billingFilters,
	);

	const fetchData = useCallback(() => {
		const page = params.get('page') || '1';
		const pageSize = params.get('pageSize') || '8';
		const direction = params.get('direction') || '';
		const orderBy = params.get('orderBy') || '';
		const searchString = params.get('searchString') || '';

		if (timeError) return;
		if (!startDate || !endDate) return;
		const data: GetBillingProperties = {
			page,
			pageSize,
			startDate,
			endDate,
			onlyNew,
			direction,
			orderBy,
			searchString,
			userId,
			clientIds,
			mbhBillingStatus,
		};

		dispatch(getBillingSessions(data)).then(unwrapResult).catch(errorNormalizer);
	}, [dispatch, params, endDate, startDate, userId, onlyNew, timeError, clientIds, mbhBillingStatus]);

	const downloadHandler = useCallback(() => {
		setShowDownloadAlert(true);
	}, []);

	const proceedWithDownload = useCallback(() => {
		const page = '1';
		const direction = params.get('direction') || '';
		const orderBy = params.get('orderBy') || '';
		const searchString = params.get('searchString') || '';
		const pageSize = sessions.totalNumberOfItems ? sessions.totalNumberOfItems.toString() : '8';

		if (!startDate || !endDate) return;

		toast(REPORT_GENERATION_MESSAGE);

		const data: GetBillingProperties = {
			page,
			pageSize,
			startDate,
			endDate,
			onlyNew,
			direction,
			orderBy,
			searchString,
			userId,
			clientIds,
			includeAdditionalData: !mbhFormat,
			mbhBillingStatus,
		};
		dispatch(downloadMBHExcelReport(data))
			.then(unwrapResult)
			.then((response) => {
				downloadFile(response);
				fetchData();
				setShowDownloadAlert(false);
			})
			.catch(errorNormalizer);
	}, [
		dispatch,
		fetchData,
		params,
		sessions,
		endDate,
		startDate,
		userId,
		onlyNew,
		clientIds,
		mbhFormat,
		mbhBillingStatus,
	]);

	const includeAdditionalDataHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		setMbhFormat(checked);
	};

	useEffect(() => {
		const timeout = setTimeout(fetchData, 1000);

		// TODO - return void as in return () => void clearTimeout(timeout);
		return () => clearTimeout(timeout);
	}, [fetchData]);

	const billingStatusHandler = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		setMbhBillingStatus(value);
	};

	return (
		<>
			<ReportFilters showUsers isMultipleSelect />
			<div
				style={{
					display: 'flex',
					gap: '16px',
					height: '104px',
					alignItems: 'center',
				}}
			>
				<div style={{ width: '175px' }}>
					<CustomSelect
						label="MBH Billing Status:"
						data={MBHBillingStatuses}
						value={mbhBillingStatus === null ? '' : `${mbhBillingStatus}`}
						setValue={billingStatusHandler}
					/>
				</div>
				<MBHStatus />
				<div
					style={{
						display: 'flex',
						width: 'max-content',
						gap: '16px',
						alignItems: 'flex-end',
						marginTop: '16px',
					}}
				>
					<CustomCheckBox
						item={{
							label: 'MBH Format:',
							id: '0',
							checked: mbhFormat,
							disabled: loading,
						}}
						onChange={includeAdditionalDataHandler}
					/>
					<CustomButton onClick={downloadHandler} title="Download" loading={loading} disabled={timeError} />
				</div>
			</div>
			<CustomTable
				headers={Headers}
				TableBody={TableBody}
				data={sessions}
				loading={loading}
				hasMobile={false}
				fixedLayout
			/>
			<CustomAlert
				open={showDownloadAlert}
				onClose={() => setShowDownloadAlert(false)}
				title="Download Warning"
				Content={({ onClose }) => <DownloadAlertContent onClose={onClose} onDownload={proceedWithDownload} />}
			/>
		</>
	);
};

export default MBHReport;
